import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardBody, CardHeader, Row, Col, Input, Label, Form, FormGroup } from 'reactstrap';
import { toast } from 'react-toastify';
import { Btn, H5 } from '../../../AbstractElements';
import config from '../../../config';
import licenseService from '../../../Services/licenseService';

const authHeader = () => {
    const token = localStorage.getItem('sessiontoken');
    return { headers: { Authorization: `${token}` } };
};

const CreatePOForm = ({ onCreated, scopedToConcessionaire = true }) => {
    const { t } = useTranslation();
    const concessionaire_id = localStorage.getItem('concessionaireId');
    const [properties, setProperties] = useState([]);
    const [groups, setGroups] = useState([]);
    const [poNumber, setPoNumber] = useState('');
    // targetValue is either "prop:<id>" or "group:<id>"
    const [targetValue, setTargetValue] = useState('');
    const [quantity, setQuantity] = useState(10);
    const [issueDate, setIssueDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (scopedToConcessionaire && concessionaire_id) {
            axios.post(`${config.hostname}/property/getpropertybyconcessionaire`,
                { property_concessionaire_id: concessionaire_id },
                authHeader())
                .then(res => setProperties(res.data || []))
                .catch(err => console.log(err));
        } else {
            axios.get(`${config.hostname}/property`, authHeader())
                .then(res => setProperties(res.data || []))
                .catch(err => console.log(err));
        }
    }, [concessionaire_id, scopedToConcessionaire]);

    useEffect(() => {
        licenseService.listGroups()
            .then(res => setGroups(res.data || []))
            .catch(err => console.log(err));
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!poNumber || !targetValue || !quantity || !issueDate) {
            toast.error(t('All fields are required'));
            return;
        }
        const [kind, id] = targetValue.split(':');
        if (!kind || !id) {
            toast.error(t('Select a target property or group'));
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                po_number: poNumber,
                po_quantity: Number(quantity),
                po_issue_date: issueDate,
                po_metadata: {}
            };
            if (kind === 'group') payload.po_license_group_id = id;
            else payload.po_property_id = id;

            const res = await licenseService.createPO(payload);
            toast.success(t('PO created. {{count}} licenses minted.', { count: res.data.pool_created }));
            setPoNumber('');
            setQuantity(10);
            if (onCreated) onCreated();
        } catch (err) {
            const msg = err.response?.data?.message || err.message || t('Failed to create PO');
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <H5 attrH5={{ className: 'mb-0' }}>{t('Create Purchase Order')}</H5>
                <div className='f-12 f-light mt-1'>
                    {t('Mints N individual licenses bound to the selected asset. License IDs follow')}
                    <code className='ms-1'>[MallCode]-[PO#]-[Sequence]</code>.
                </div>
            </CardHeader>
            <CardBody>
                <Form className='theme-form' onSubmit={submit}>
                    <Row>
                        <Col md='6'>
                            <FormGroup>
                                <Label>{t('PO Number')}</Label>
                                <Input value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder='PO-2026-Q2-001' />
                            </FormGroup>
                        </Col>
                        <Col md='6'>
                            <FormGroup>
                                <Label>{t('Target')}</Label>
                                <Input type='select' value={targetValue} onChange={e => setTargetValue(e.target.value)}>
                                    <option value=''>{t('Select property or group…')}</option>
                                    {groups.length > 0 && (
                                        <optgroup label={t('License Groups (shared pool)')}>
                                            {groups.map(g => (
                                                <option key={g._id} value={`group:${g._id}`}>
                                                    {g.group_name} · {g.group_code} ({t('{{count}} members', { count: g.members ? g.members.length : 0 })})
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                    <optgroup label={t('Properties (asset-wise)')}>
                                        {properties
                                            .filter(p => !p.property_license_group_id)
                                            .map(p => (
                                                <option key={p._id} value={`prop:${p._id}`}>
                                                    {p.property_name} · {p.property_mall_code}
                                                </option>
                                            ))}
                                    </optgroup>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md='6'>
                            <FormGroup>
                                <Label>{t('Quantity')}</Label>
                                <Input type='number' min='1' value={quantity} onChange={e => setQuantity(e.target.value)} />
                            </FormGroup>
                        </Col>
                        <Col md='6'>
                            <FormGroup>
                                <Label>{t('Issue Date')}</Label>
                                <Input type='date' value={issueDate} onChange={e => setIssueDate(e.target.value)} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <div className='text-end'>
                        <Btn attrBtn={{ color: 'primary', type: 'submit', disabled: submitting }}>
                            {submitting ? t('Minting…') : t('Create PO & Mint Licenses')}
                        </Btn>
                    </div>
                </Form>
            </CardBody>
        </Card>
    );
};

export default CreatePOForm;
